import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
  Input,
  Button,
  useToast,
  Flex,
  Text,
  Box,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  Alert,
  AlertIcon,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import { MdContentCopy, MdCheck, MdInfo } from 'react-icons/md';
import { Project } from '../../../types/project';
import { ProjectService } from '../../../services/projectService';
import { useTranslation } from 'react-i18next';
import { buildShareUrl } from '../../../atoms/shared/regionAtom';

interface ProjectShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
}

export const ProjectShareModal: React.FC<ProjectShareModalProps> = ({
  isOpen,
  onClose,
  project,
}) => {
  const { t } = useTranslation(['v0/projects', 'v0/common', 'common']);
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  // 状态
  const [isPublic, setIsPublic] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // 当弹窗打开时，检查项目是否已经公开分享
  useEffect(() => {
    if (isOpen && project) {
      setIsPublic(!!project.isPublic && !!project.shareId);
      if (project.shareId) {
        // 使用域名工具函数生成分享链接
        const url = buildShareUrl(`/s/project/${project.shareId}`);
        setShareUrl(url);
      } else {
        setShareUrl('');
      }
      setError(null);
      setCopied(false);
    }
  }, [isOpen, project]);

  // 处理分享状态切换
  const handleShareToggle = async (newValue: boolean) => {
    setLoading(true);
    setError(null);

    try {
      let updatedProject;

      if (newValue) {
        // 分享项目
        updatedProject = await ProjectService.shareProject(project.id);
        toast({
          title: t('v0/projects:share.projectShared'),
          description: t('v0/projects:share.projectSharedDescription'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // 取消分享
        updatedProject = await ProjectService.unshareProject(project.id);
        toast({
          title: t('v0/projects:share.projectUnshared'),
          description: t('v0/projects:share.projectUnsharedDescription'),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }

      setIsPublic(!!updatedProject.isPublic && !!updatedProject.shareId);

      if (updatedProject.shareId) {
        // 使用域名工具函数生成分享链接
        const url = buildShareUrl(`/s/project/${updatedProject.shareId}`);
        setShareUrl(url);
      } else {
        setShareUrl('');
      }
    } catch (err) {
      console.error('Error toggling project share:', err);
      setError(err instanceof Error ? err.message : t('v0/common:errors.operationFailed'));
      // 恢复原来的状态
      setIsPublic(!newValue);
    } finally {
      setLoading(false);
    }
  };

  // 复制分享链接
  const handleCopyLink = () => {
    if (!shareUrl) return;

    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setCopied(true);
        toast({
          title: t('v0/projects:share.linkCopied'),
          status: 'success',
          duration: 2000,
          isClosable: true,
        });

        // 3秒后重置复制状态
        setTimeout(() => {
          setCopied(false);
        }, 3000);
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        toast({
          title: t('v0/projects:share.copyFailed'),
          description: t('v0/projects:share.copyFailedDescription'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
  };

  // 选择输入框内容
  const handleInputClick = () => {
    inputRef.current?.select();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="xl" mx={4}>
        <ModalHeader>{t('v0/projects:share.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {error && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <FormControl display="flex" alignItems="center" mb={6}>
            <FormLabel htmlFor="share-toggle" mb="0" flex="1">
              {t('v0/projects:share.publicShare')}
              <Tooltip
                label={t('v0/projects:share.publicShareTooltip')}
                placement="top"
                hasArrow
              >
                <MdInfo style={{ marginLeft: '4px', color: 'var(--chakra-colors-gray-500)', fontSize: '14px' }} />
              </Tooltip>
            </FormLabel>
            <Switch
              id="share-toggle"
              isChecked={isPublic}
              onChange={(e) => handleShareToggle(e.target.checked)}
              colorScheme="green"
              isDisabled={loading}
            />
          </FormControl>

          {isPublic && (
            <Box>
              <Text mb={2} fontWeight="medium">
                {t('v0/projects:share.shareLink')}
              </Text>
              <InputGroup size="md">
                <Input
                  ref={inputRef}
                  value={shareUrl}
                  isReadOnly
                  pr="4.5rem"
                  onClick={handleInputClick}
                  borderRadius="md"
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={handleCopyLink}
                    colorScheme={copied ? "green" : "blue"}
                    variant={copied ? "solid" : "outline"}
                    rightIcon={copied ? <MdCheck /> : <MdContentCopy />}
                  >
                    {copied ? t('common:actions.copied') : t('v0/common:copy')}
                  </Button>
                </InputRightElement>
              </InputGroup>

              <Alert status="info" mt={4} borderRadius="md" size="sm">
                <AlertIcon />
                <Text fontSize="sm">
                  {t('v0/projects:share.shareWarning')}
                </Text>
              </Alert>
            </Box>
          )}

          {loading && (
            <Flex justify="center" mt={4}>
              <Spinner size="sm" mr={2} />
              <Text>{t('v0/common:processing')}...</Text>
            </Flex>
          )}

          <Flex justify="flex-end" mt={6}>
            <Button onClick={onClose}>
              {t('v0/common:actions.close')}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
